import { useMutation, useQuery } from '@tanstack/react-query'
import {
  fetchOnboardingTrace,
  createOnboardingTrace,
  updateOnboardingTrace,
} from '@/services/onboarding.services'
import {
  ONBOARDING_TRACE_QUERY_KEY,
  CACHE_STALE_TIME,
  CACHE_GC_TIME,
  QUERY_NETWORK_MODE,
} from '@/shared/constants/cache'
import { queryClient } from '@/shared/lib/tanstack'
import type {
  OnboardingTrace,
  OnboardingTraceFilters,
} from '@/shared/types/global.types'

export function useOnboardingTrace(
  filters: OnboardingTraceFilters,
  enabled: boolean = true,
) {
  const queryKey = [ONBOARDING_TRACE_QUERY_KEY, filters]

  const invalidateOnboardingTrace = () =>
    queryClient.invalidateQueries({ queryKey })

  const query = useQuery<OnboardingTrace | undefined>({
    queryKey,
    queryFn: () => fetchOnboardingTrace(filters),
    enabled,
    staleTime: CACHE_STALE_TIME,
    gcTime: CACHE_GC_TIME,
    networkMode: QUERY_NETWORK_MODE,
  })

  const createMutation = useMutation({
    mutationFn: (productType: OnboardingTrace['product_type']) =>
      createOnboardingTrace(productType),
    onSuccess: invalidateOnboardingTrace,
    onError: console.error,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: Partial<Pick<OnboardingTrace, 'step' | 'status'>>
    }) => updateOnboardingTrace(id, body),
    onSuccess: invalidateOnboardingTrace,
    onError: console.error,
  })

  return { ...query, createMutation, updateMutation }
}
